import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfigValuesState } from './useConfigValue';
import { PeriodDay } from '../types/PeriodDay';
import { getPeriodDaysEnabled } from '../helpers/periodDays';
import { convertHoursToProcent, getCapacityPrDay } from '../components/WeekOverview/Cell.helpers';
import { NumberMax2DecimalFormatAsString } from '../components/Utils';
import { InputMode, WorkDayDto } from '../api/generated/data-contracts';
import { TFunction } from 'i18next';

export type AllocationFormatter = (hours: number | null | undefined) => string;

type FormatterInputs = {
    inputMode: InputMode | undefined;
    workWeek: WorkDayDto[];
    perioddays: PeriodDay[];
    availability: number;
    t: TFunction<'timesheet', undefined>;
};

export const buildAllocationFormatter = ({ inputMode, workWeek, perioddays, availability, t }: FormatterInputs): AllocationFormatter => {
    if (inputMode !== 'Percent') {
        return hours => (hours ? NumberMax2DecimalFormatAsString(hours) : '');
    }

    const compatWeekConfig = workWeek.map(d => ({ name: d.dayOfWeek, enabled: d.isWorkingDay }));
    const workingDays = getPeriodDaysEnabled(perioddays, t, compatWeekConfig);
    if (workingDays.length === 0 || availability <= 0) return () => '';

    const hoursPrDay = getCapacityPrDay(workingDays, availability);
    if (hoursPrDay <= 0) return () => '';

    return hours => {
        if (hours == null || hours === 0) return '';
        const pct = convertHoursToProcent(hours, workingDays.length, hoursPrDay);
        return `${NumberMax2DecimalFormatAsString(pct)} %`;
    };
};

export const useAllocationFormatter = (perioddays: PeriodDay[]): AllocationFormatter => {
    const { t } = useTranslation('timesheet');
    const {
        initialData: {
            configuration: { inputMode, workWeek, capacityDefault },
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const availability = user?.availability ?? user?.calendar?.dailyCapacity ?? capacityDefault ?? 0;
    const resolvedWorkWeek = workWeek ?? [];

    return useMemo(
        () => buildAllocationFormatter({ inputMode, workWeek: resolvedWorkWeek, perioddays, availability, t }),
        [inputMode, resolvedWorkWeek, availability, perioddays, t],
    );
};
